import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import type { LocationSearchParams } from '../../hooks/search/useLocationSearchParams';
import type { Supplier } from '../../types/Supplier';
import { getLatLng } from '../../util/SupplierUtils';
import { MARKET_RADIUS_MILES, findMarketByCity, marketPath, milesBetween } from '../../seo/markets.mjs';
import { geoLine, stateAbbr } from '../location/profile/LocationProfileSEO';
import { DEFAULT_IMAGE, useClaimHead } from '../seo/HeadDefaults';

const SITE = 'https://secur.space';
const ITEM_LIST_MAX = 50;

interface SearchSEOProps {
  locationParams: LocationSearchParams | undefined;
  /** The yards currently listed on the page, in display order. */
  locations?: Supplier[];
  /**
   * What the supplier fetch returned, once it has settled successfully; undefined
   * while loading or after a failure. Not the listed yards: those also depend on
   * the map viewport and filters, and a cached search returns yards nationwide.
   */
  fetchedYards?: Supplier[];
}

/** Lead with what people type ("truck parking los angeles"), then the rest of the offer. */
export const searchTitle = (city: string, state: string): string =>
  `Truck Parking & Container Storage in ${city}, ${stateAbbr(state)} | SecurSpace`;

export const searchDescription = (city: string, state: string): string =>
  `Truck parking, trailer parking and container storage in ${city}, ${stateAbbr(state)}. Compare secure yards, daily and monthly prices, amenities and instant booking on SecurSpace.`;

/**
 * schema.org ItemList of the yards shown for a city, so Google reads the page
 * as a collection about that city rather than a generic search form. Capped so
 * the JSON stays small; the page itself still lists everything.
 */
export const buildSearchItemList = (city: string, state: string, url: string, locations: Supplier[]) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: geoLine(city, state),
  url,
  numberOfItems: locations.length,
  itemListElement: locations.slice(0, ITEM_LIST_MAX).map((l, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    url: `${SITE}/location-profile/${l.locationId || l.id}`,
    name: l.locationName,
  })),
});

/**
 * The market whose city page this is, if the page is indexable; undefined otherwise.
 * Only a market's own city page is indexable (securspace-ui#3049), and only while
 * it has a live yard within the market radius. Every other search page (suburb,
 * dragged map, bare /yards) is noindex, so Google indexes one page per market.
 * Undecided while the yards load or after a failed fetch: a slow or broken API
 * must never noindex a market page.
 */
export const indexableMarket = (locationParams: LocationSearchParams | undefined, fetchedYards: Supplier[] | undefined) => {
  const market = findMarketByCity(locationParams?.city, stateAbbr(locationParams?.state), locationParams?.country);
  if (!market || !fetchedYards) return market;
  const hasYard = fetchedYards.some((yard) => {
    const at = getLatLng(yard);
    return !!yard.visible && !!at && milesBetween(market, at) <= MARKET_RADIUS_MILES;
  });
  return hasYard ? market : undefined;
};

const SearchSEO = ({ locationParams, locations = [], fetchedYards }: SearchSEOProps) => {
  useClaimHead();
  // The bare /yards and dragged-map pages fill a city into the address themselves
  // (the visitor's own, or the prerender host's). Only an address that arrived with
  // a city is a city page.
  const [openedOnCity] = useState(() => !!(locationParams?.city && locationParams?.state));
  const market = openedOnCity ? indexableMarket(locationParams, fetchedYards) : undefined;
  const indexable = !!market;
  // A market page is named the way the market list names it, so address variants
  // (state=California, other case or spacing) share one canonical.
  const city = market?.city || locationParams?.city || '';
  const state = market?.state || locationParams?.state || '';
  const hasLocation = !!city && !!state;
  // The clean city address (securspace-ui#3052); /search only redirects to it (#3054).
  const pageUrl = `${SITE}${marketPath({ city, state: stateAbbr(state), country: market?.country || locationParams?.country || 'US' })}`;

  const title = hasLocation ? searchTitle(city, state) : 'Truck Parking & Container Storage Near You | SecurSpace';
  const description = hasLocation
    ? searchDescription(city, state)
    : 'Truck parking, trailer parking and container storage across the United States. Compare secure yards, prices and amenities and book on SecurSpace.';
  // No canonical on a noindex page: noindex plus a canonical elsewhere are conflicting signals.
  const canonicalUrl = indexable ? pageUrl : undefined;
  const keywords = hasLocation
    ? `truck parking ${city}, container storage ${stateAbbr(state)}, trailer parking, intermodal storage, ${city} ${stateAbbr(state)}`
    : 'truck parking, container storage, trailer parking, intermodal storage';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {!indexable && <meta name="robots" content="noindex,follow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SecurSpace" />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      {hasLocation && locations.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(buildSearchItemList(city, state, pageUrl, locations))}
        </script>
      )}
    </Helmet>
  );
};

export default SearchSEO;
